import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "../axiosConfig";
import Modal from "../components/Modal";
import AccountSelector from "../components/AccountSelector";
import Table from "../components/Table";
import { InlineEdit, InlineEditCheckbox, locationLabel } from "../components/Utils";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Checkbox from '../components/Checkbox';
import PageTitle from "../components/PageTitle";

const COUNTRY_OPTIONS_ORDER = ["GB", "US", "|", "..."];

const initialFormState = {
  title: "",
  first_name: "",
  last_name: "",
  account: null,
  office: "",
  job_title: "",
  email: "",
  office_phone: "",
  mobile_phone: "",
  tutor: false,
  attendee_only: false,
  temp_public_course: false,
  status: true,
};

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]); // <-- store account locations
  const [allOffices, setAllOffices] = useState([]); // <-- every office, for the table's office selector
  const [loading, setLoading] = useState(true);
  const [showAllColumns, setShowAllColumns] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/contacts/", { withCredentials: true });
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    axios
      .get("/locations/", { withCredentials: true })
      .then((res) => setAllOffices(res.data || []))
      .catch((err) => console.error("Error fetching offices:", err));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAccountChange = async (account) => {
    setFormData((prev) => ({ ...prev, account, office: "" })); // reset office when account changes
    setLocations([]);

    if (account?.id) {
      try {
        const response = await axios.get(`/accounts/${account.id}/`, {
          withCredentials: true,
        });
        if (response.data.locations) {
          setLocations(response.data.locations);
        } else {
          setLocations([]);
        }
      } catch (error) {
        console.error("Error fetching account info:", error);
      }
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setLocations([]);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        "/contacts/",
        {
          ...formData,
          account_id: formData.account?.id || null,
          office: formData.office || "", // <-- send office id
        },
        { withCredentials: true },
      );

      if (response.data.success) {
        setShowModal(false);
        resetForm();
        fetchContacts();
      }
    } catch (error) {
      console.error("Error creating contact:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleInlineSave = async (contactId, field, newValue) => {
    try {
      const response = await axios.patch(
        `/contacts/${contactId}/`,
        { [field]: newValue },
        { withCredentials: true },
      );
      if (response.data.success) {
        setContacts((prev) =>
          prev.map((c) =>
            c.id === contactId ? { ...c, [field]: newValue } : c,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to update contact field:", error);
    }
  };

  const handleOfficeSave = async (contactId, field, newValue) => {
    try {
      const response = await axios.patch(
        `/contacts/${contactId}/`,
        { office: newValue || null },
        { withCredentials: true },
      );
      if (response.data.success) {
        const officeId = newValue ? parseInt(newValue, 10) : null;
        const officeRow = officeId
          ? allOffices.find((o) => o.id === officeId)
          : null;
        const office = officeRow
          ? {
              id: officeRow.id,
              address_1: officeRow.address_1,
              city: officeRow.city,
              country: officeRow.country,
              switchboard_number: officeRow.switchboard_number,
            }
          : null;
        setContacts((prev) =>
          prev.map((c) => (c.id === contactId ? { ...c, office } : c)),
        );
      }
    } catch (error) {
      console.error("Failed to update contact office:", error);
    }
  };

  const handleAccountSave = async (contactId, field, newValue) => {
    try {
      const response = await axios.patch(
        `/contacts/${contactId}/`,
        { account_id: newValue?.id || null },
        { withCredentials: true },
      );
      if (response.data.success) {
        setContacts((prev) =>
          prev.map((c) =>
            c.id === contactId
              ? {
                  ...c,
                  account_id: newValue?.id || null,
                  account_name_short: newValue?.name_short || null,
                  // Backend clears the contact's office when the account changes.
                  office: null,
                }
              : c,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to update contact account:", error);
    }
  };

  const baseColumns = [
    {
      key: "name",
      header: "Name",
      type: "alphabetical",
      fixed: true,
      width: 144,
      render: (value, row) => (
        <Link to={`/contact?id=${row.id}`}>
          {row.first_name} {row.last_name}
        </Link>
      ),
    },
    {
      key: "account_name",
      header: "Account",
      type: "alphabetical",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="account"
          type="account"
          value={
            row.account_id
              ? { id: row.account_id, name_short: row.account_name_short }
              : null
          }
          displayValue={
            row.account_id ? (
              <Link to={`/account?id=${row.account_id}`}>
                {row.account_name_short || "Unnamed Account"}
              </Link>
            ) : (
              ""
            )
          }
          onSave={(field, newValue) =>
            handleAccountSave(row.id, field, newValue)
          }
        />
      ),
    },
    {
      key: "job_title",
      header: "Job Title",
      type: "static",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="job_title"
          value={row.job_title || ""}
          onSave={(field, newValue) =>
            handleInlineSave(row.id, field, newValue)
          }
        />
      ),
    },
    {
      key: "email",
      header: "Email",
      type: "static",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="email"
          type="email"
          value={row.email || ""}
          onSave={(field, newValue) =>
            handleInlineSave(row.id, field, newValue)
          }
          quickEmailParticipant={{
            id: row.id,
            type: "contact",
            first_name: row.first_name,
            last_name: row.last_name,
          }}
        />
      ),
    },
  ];

  const additionalColumns = [
    {
      key: "status",
      header: "Active",
      type: "discrete",
      width: 80,
      render: (value, row) => (
        <InlineEditCheckbox
          field="status"
          value={row.status}
          onSave={(field, checked) =>
            handleInlineSave(row.id, field, checked ? 1 : 0)
          }
        />
      ),
    },
    {
      key: "office",
      header: "Location",
      type: "static",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="office"
          type="dropdown"
          value={row.office?.id ? String(row.office.id) : ""}
          displayValue={locationLabel(row.office)}
          options={allOffices
            .filter((o) => o.account_id === row.account_id)
            .map((o) => ({
              value: String(o.id),
              label: locationLabel(o),
            }))}
          onSave={(field, newValue) =>
            handleOfficeSave(row.id, field, newValue)
          }
        />
      ),
    },
    {
      key: "office_city",
      header: "Location City",
      type: "static",
      width: 128,
      render: (value, row) => (
        <InlineEdit
          field="office_city"
          value={row.office?.city || ""}
          disabled
          disabled_reason="Change location city on account page"
        />
      ),
    },
    {
      key: "office_country",
      header: "Location Country",
      type: "static",
      width: 128,
      render: (value, row) => (
        <InlineEdit
          field="office_country"
          value={row.office?.country || ""}
          disabled
          disabled_reason="Change location country on account page"
        />
      ),
    },
    {
      key: "office_switchboard",
      header: "Location Switchboard",
      type: "static",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="office_switchboard"
          type="phone"
          value={row.office?.switchboard_number || ""}
          phoneCountry={row.office?.country}
          disabled
          disabled_reason="Change location switchboard on account page"
        />
      ),
    },
    {
      key: "office_phone",
      header: "Office Phone",
      type: "static",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="office_phone"
          type="phone"
          value={row.office_phone || ""}
          onSave={(field, newValue) =>
            handleInlineSave(row.id, field, newValue)
          }
        />
      ),
    },
    {
      key: "mobile_phone",
      header: "Mobile Phone",
      type: "static",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="mobile_phone"
          type="phone"
          value={row.mobile_phone || ""}
          onSave={(field, newValue) =>
            handleInlineSave(row.id, field, newValue)
          }
        />
      ),
    },
  ];

  const columns = showAllColumns
    ? [...baseColumns, ...additionalColumns]
    : baseColumns;

  // Transform data to include computed fields for sorting
  const tableData = contacts.map((contact) => ({
    ...contact,
    name: `${contact.first_name} ${contact.last_name}`,
    account_name: contact.account_name_short || "",
  }));

  const getPhoneInputValue = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return "";
    if (value.startsWith("+")) return value;

    // Legacy values were stored as digits only, with no dial code to prefix.
    return value.replace(/\D/g, "");
  };

  return (
    <div className="page-layout">
      <PageTitle>Contacts</PageTitle>
      <div className="page-header">
        <div className="page-header-buttons">
          <button onClick={() => setShowAllColumns((prev) => !prev)}>
            {showAllColumns ? "Show Less" : "Show All"}
          </button>
          <button onClick={() => setShowModal(true)}>+ New Contact</button>
        </div>
      </div>

      <div className="page-content">
        <Table
          data={tableData}
          columns={columns}
          loading={loading}
          emptyMessage="No contacts found."
          defaultSortColumn="name"
          defaultSortDirection="asc"
          fillPage
        />

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Create New Contact"
        >
          <form
            className="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <label>
              Title:
              <input
                autoComplete="off"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
            </label>

            <label>
              First Name:
              <input
                autoComplete="off"
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Last Name:
              <input
                autoComplete="off"
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Company:
              <AccountSelector
                value={formData.account}
                onChange={handleAccountChange}
              />
            </label>

            <label>
              Location:
              <select
                autoComplete="off"
                name="office"
                value={formData.office || ""}
                onChange={handleChange}
                disabled={locations.length === 0}
              >
                {locations.length === 0 ? (
                  <option value="">No locations available</option>
                ) : (
                  <>
                    <option value="">Select one...</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {locationLabel(loc)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </label>

            <label>
              Job Title:
              <input
                autoComplete="off"
                type="text"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
              />
            </label>

            <label>
              Email:
              <input
                autoComplete="off"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </label>

            <label>
              Office Phone:
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <PhoneInput
                  international
                  name="office_phone"
                  countryOptionsOrder={COUNTRY_OPTIONS_ORDER}
                  value={getPhoneInputValue(formData.office_phone)}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      office_phone: value || "",
                    }))
                  }
                  placeholder="Enter phone number"
                  style={{ flex: 1 }}
                />
              </div>
            </label>

            <label>
              Mobile Phone:
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <PhoneInput
                  international
                  name="mobile_phone"
                  countryOptionsOrder={COUNTRY_OPTIONS_ORDER}
                  value={getPhoneInputValue(formData.mobile_phone)}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      mobile_phone: value || "",
                    }))
                  }
                  placeholder="Enter phone number"
                  style={{ flex: 1 }}
                />
              </div>
            </label>

            <div className="checkbox-group">
              <label>
                Tutor
                <Checkbox
                  name="tutor"
                  checked={formData.tutor}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="checkbox-group">
              <label>
                Attendee Only
                <Checkbox
                  name="attendee_only"
                  checked={formData.attendee_only}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="checkbox-group">
              <label>
                Public Course
                <Checkbox
                  name="temp_public_course"
                  checked={formData.temp_public_course}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="checkbox-group">
              <label>
                Active
                <Checkbox
                  name="status"
                  checked={formData.status}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="modal-footer">
              <button type="submit" className="submit-btn" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default Contacts;
